import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import { logout } from "../../app/auth/authSlice.js";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import CustomModal from "../../components/CustomComponents/modal/customModal";

const ManageSmsPackages = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const themeColor = useThemeColor();

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusConfirmPackage, setStatusConfirmPackage] = useState(null);

  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_SMS_PACKAGES);

      if (response.data.status) {
        setPackages(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        showErrorToast(response.data.message || "Failed to fetch SMS packages");
        setPackages([]);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to fetch SMS packages");
      }
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const confirmPackageStatusChange = async () => {
    if (!statusConfirmPackage) return;

    const id = statusConfirmPackage.smsPackageID ?? statusConfirmPackage.SmsPackageID;
    const pStatus = statusConfirmPackage.isActive ?? statusConfirmPackage.IsActive;
    const pName = statusConfirmPackage.name ?? statusConfirmPackage.Name;
    const newStatus = !pStatus;

    setStatusConfirmPackage(null);

    try {
      setLoading(true);
      const response = await axiosInstance.patch(
        APIs.PATCH_UPDATE_SMS_PACKAGE_STATUS,
        null,
        {
          params: {
            smsPackageID: id,
            status: newStatus,
          },
        }
      );

      if (response.data.status) {
        showSuccessToast(
          `"${pName || "SMS package"}" is now ${newStatus ? "Active" : "InActive"}.`
        );
        fetchPackages();
      } else {
        showErrorToast(response.data.message || "Failed to update status");
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      showErrorToast("Failed to update SMS package status");
    }
  };

  const formatPrice = (pkg) => {
    const sell = parseFloat(pkg.price ?? pkg.Price ?? 0) || 0;
    const list = parseFloat(pkg.actualPrice ?? pkg.ActualPrice ?? 0) || 0;
    const vat = parseFloat(pkg.vatPercent ?? pkg.VatPercent ?? 0);
    const vatPart = vat > 0 ? ` + ${vat}% VAT` : "";
    if (list > sell) {
      return (
        <>
          <span className="text-muted text-decoration-line-through me-1">
            £{list.toFixed(2)}
          </span>
          <strong>£{sell.toFixed(2)}</strong>
          {vatPart}
        </>
      );
    }
    return `£${sell.toFixed(2)}${vatPart}`;
  };

  return (
    <div className="content">
      {loading && <Preloader />}
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <Link to="/dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">SMS Packages</li>
                </ol>
                <h1 className="page-header mb-0">SMS Packages</h1>
                <p className="text-muted mb-0 small">
                  Dedicated top-up catalog for EPOS (separate from subscription Packages).
                </p>
              </div>
              <div className="ms-auto">
                <button
                  className="btn px-4"
                  onClick={() => navigate("/smsPackages/add")}
                  style={{
                    backgroundColor: themeColor,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    border: "none",
                    color: "white",
                    borderRadius: "25px",
                    fontWeight: "500",
                  }}
                >
                  <i className="fa fa-plus fa-lg me-2 ms-n2"></i>
                  Add SMS Package
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="bg-light">
                      <tr>
                        <th className="pt-2 pb-2">Name</th>
                        <th className="pt-2 pb-2">SMS</th>
                        <th className="pt-2 pb-2">Price</th>
                        <th className="pt-2 pb-2">Card style</th>
                        <th className="pt-2 pb-2 text-center">Status</th>
                        <th className="pt-2 pb-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="text-center">
                            <Preloader />
                          </td>
                        </tr>
                      ) : packages.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No SMS packages yet. Add packs to match the EPOS catalog.
                          </td>
                        </tr>
                      ) : (
                        packages.map((pkg) => {
                          const id = pkg.smsPackageID ?? pkg.SmsPackageID;
                          const name = pkg.name ?? pkg.Name;
                          const detail = pkg.description ?? pkg.Description;
                          const qty = pkg.smsQuantity ?? pkg.SmsQuantity;
                          const style = pkg.cardStyle ?? pkg.CardStyle ?? "Default";
                          const active = pkg.isActive ?? pkg.IsActive;
                          return (
                            <tr key={id}>
                              <td>
                                <strong>{name}</strong>
                                {detail && (
                                  <div className="text-muted small">{detail}</div>
                                )}
                              </td>
                              <td>{qty} SMS</td>
                              <td>{formatPrice(pkg)}</td>
                              <td>{style}</td>
                              <td className="text-center">
                                <StatusBadge
                                  isActive={!!active}
                                  onClick={() =>
                                    setStatusConfirmPackage({
                                      smsPackageID: id,
                                      isActive: active,
                                      name,
                                    })
                                  }
                                />
                              </td>
                              <td className="text-center">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => navigate(`/smsPackages/update/${id}`)}
                                  title="Edit"
                                >
                                  <i className="fa fa-edit"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="text-muted mt-3 mb-0">
                  Showing {packages.length} SMS package(s)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        title="Change SMS package status?"
        subtitle={
          statusConfirmPackage
            ? `Set "${statusConfirmPackage.name || "this package"}" to ${
                statusConfirmPackage.isActive ? "InActive" : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setStatusConfirmPackage(null)}
        isOpen={!!statusConfirmPackage}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmPackageStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </div>
  );
};

export default ManageSmsPackages;
